import * as React from "react";
/**
 * Evento custom usato per tenere allineate tutte le istanze montate nella
 * stessa tab: l'evento nativo "storage" viene emesso solo sulle ALTRE tab.
 */
const AUTOPLAY_EVENT = "autoplay-storage-change";
function readFromStorage(key, fallback) {
    // SSR / storage disabilitato (Safari private mode, cookie bloccati, ...)
    if (typeof window === "undefined")
        return fallback;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === null)
            return fallback;
        return raw === "true" || raw === "1";
    }
    catch {
        return fallback;
    }
}
function writeToStorage(key, value) {
    try {
        window.localStorage.setItem(key, String(value));
    }
    catch {
        // storage pieno o non disponibile: l'autoplay resta valido solo in memoria
    }
}
/**
 * Legge/scrive il flag di autoplay in localStorage.
 *
 * @param key chiave di localStorage (default: "autoplay")
 * @param defaultValue valore usato se la chiave non esiste ancora
 * @returns [valore, setter] con la stessa firma di useState
 */
export function useAutoplay(key = "autoplay", defaultValue = true) {
    const [enabled, setEnabled] = React.useState(() => readFromStorage(key, defaultValue));
    // Se cambia la chiave, rileggo il valore corrispondente
    React.useEffect(() => {
        setEnabled(readFromStorage(key, defaultValue));
    }, [key]);
    // Sincronizzazione tra istanze (stessa tab) e tra tab diverse
    React.useEffect(() => {
        const handleCustom = (event) => {
            const detail = event.detail;
            if (detail?.key === key) {
                setEnabled(detail.value);
            }
        };
        const handleStorage = (event) => {
            if (event.key === key) {
                setEnabled(readFromStorage(key, defaultValue));
            }
        };
        window.addEventListener(AUTOPLAY_EVENT, handleCustom);
        window.addEventListener("storage", handleStorage);
        return () => {
            window.removeEventListener(AUTOPLAY_EVENT, handleCustom);
            window.removeEventListener("storage", handleStorage);
        };
    }, [key, defaultValue]);
    const setAutoplay = React.useCallback((value) => {
        setEnabled(value);
        writeToStorage(key, value);
        window.dispatchEvent(new CustomEvent(AUTOPLAY_EVENT, {
            detail: { key, value },
        }));
    }, [key]);
    return [enabled, setAutoplay];
}
/**
 * Lettura "one shot" fuori da React (es. dal VideoPlayer, alla fine del video).
 */
export function getAutoplay(key = "autoplay", defaultValue = true) {
    return readFromStorage(key, defaultValue);
}
